/**
 * Crypto-First Deposit API client (v2).
 *
 * Thin axios wrappers around the new /api/payments/crypto/* endpoints.
 * Lives alongside src/api/member.js but separated so the new feature
 * can ship without touching member.js.
 */
import { api, idempotencyHeaders } from './client';

/**
 * Live USD estimate for a crypto amount. Called on every keystroke
 * (debounced by the caller).
 *
 * @param {string} coin    "LTC" | "DOGE" | "TRX"
 * @param {string|number} crypto_amount  decimal string preferred
 * @returns {Promise<{
 *   coin: string,
 *   crypto_amount: string,
 *   usd_amount: string,
 *   usd_amount_cents: number,
 *   meets_minimum: boolean,
 *   minimum_usd_cents: number,
 * }>}
 */
export const estimateCryptoDeposit = (coin, crypto_amount) =>
  api
    .post('/api/payments/crypto/estimate', {
      coin,
      crypto_amount: String(crypto_amount),
    })
    .then((r) => r.data);

/**
 * Create a single-use crypto deposit. Returns the unique pay address,
 * exact pay amount, and a deposit_id the frontend polls.
 *
 * @param {string} coin
 * @param {string|number} crypto_amount
 * @returns {Promise<{
 *   deposit_id: string,
 *   payment_id: string,
 *   pay_address: string,
 *   pay_amount: string,
 *   pay_currency: string,
 *   price_usd_cents: number,
 *   status: string,
 * }>}
 */
export const createCryptoDeposit = (coin, crypto_amount) =>
  api
    // One key per click: a retry of the same click (e.g. after a lost reply) never opens a second deposit.
    .post(
      '/api/payments/crypto/deposit',
      {
        coin,
        crypto_amount: String(crypto_amount),
      },
      { headers: idempotencyHeaders() },
    )
    .then((r) => r.data);

/**
 * List the current user's v2 deposits, newest first. Polled by the
 * deposit-address screen so the UI can flip to "confirmed" the moment
 * the IPN credits a deposit.
 *
 * @param {number} limit
 * @returns {Promise<{ items: object[], count: number }>}
 */
export const listMyCryptoDeposits = (limit = 20) => api.get('/api/payments/crypto/me/deposits', { params: { limit } }).then((r) => r.data);
